/* global __APP_VERSION__ */

import { useCallback } from 'react'
import { getAirspaceMenuLabel } from '@modules/MapScreen/components/MapAirspace/airspaceTypeRegistry'
import { Assets, MenuGroup, MenuItem } from '@nexds/web'

import { InjectionTokens } from '@/controller/tokens'

import { AirspaceType, isKnownAirspaceType } from '@/domain/protocols/Airspaces'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'
import { ReviewProps, SendReviewUseCase } from '@/domain/useCases/General/SendReviewUseCase'

import { IAirspacesRepository } from '@/data/AirspacesRepository/IAirspacesRepository'

import { ToolContainer } from '../ToolbarTools.styles'
import { Icon } from './AirspacesTool.styles'
import {
  FcaAdSuggestionValidationMessages,
  validateFcaAdFrequency,
  validateFcaAdIcao,
  validateFcaAdSuggestion
} from './fcaAdSuggestionValidation'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'
import { BackdropImperativeInterface } from '@/presentation/interfaces/BackdropImperativeInterface'
import { sendMetrics } from '@/presentation/utils/sendMetrics'
import { appZIndex } from '@/presentation/utils/zIndexMapper'
import { GlobalToastUtil } from '@/utils/globalToastUtil'
import { ModalUtil } from '@/utils/modalUtil'

function AirspacesTool() {
  const VERSION = __APP_VERSION__
  const { t } = useI18n()
  const [mapLayerState, mapLayerStateMutator] = useGlobalState<MapLayerState, MapLayerStateProps>(
    InjectionTokens.MapLayerState
  )
  const airspaceRepository = useInjection<IAirspacesRepository>(InjectionTokens.AirspaceRepository)

  // Tipos de airspace não catalogados que o dataset trouxe (fora do conjunto
  // conhecido) — renderizados como toggles genéricos.
  const extraAirspaceTypes = airspaceRepository.getAvailableTypes().filter((type) => !isKnownAirspaceType(type))

  const handleAirspaces = useCallback(
    (mapLayerName: AirspaceType, concurrent?: AirspaceType, associated?: AirspaceType[]) => {
      mapLayerStateMutator.setLayer(mapLayerName, !mapLayerState[mapLayerName])
      if (concurrent) mapLayerStateMutator.setLayer(concurrent, false)

      if (associated && !mapLayerState[mapLayerName]) {
        const associatedLayersMapped: Map<AirspaceType, boolean> = new Map()
        associated.forEach((layer) => {
          associatedLayersMapped.set(layer, true)
        })
        mapLayerStateMutator.setSeveralAirspaces(associatedLayersMapped)
      }
    },
    [mapLayerState, mapLayerStateMutator]
  )

  const handleShowSuggestionModal = useCallback(() => {
    const validationMessages: FcaAdSuggestionValidationMessages = {
      requiredIcao: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_ICAO_REQUIRED'),
      invalidIcao: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_ICAO_INVALID'),
      requiredFrequency: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_FREQUENCY_REQUIRED'),
      invalidFrequency: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_FREQUENCY_INVALID'),
      frequencyOutOfRange: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_FREQUENCY_RANGE')
    }

    const handleSendFcaAdSuggestion = async (adCode: string, frequency: string) => {
      const validationResult = validateFcaAdSuggestion(adCode, frequency, validationMessages)
      if (!validationResult.isValid) return false

      BackdropImperativeInterface.show(appZIndex.GLOBAL_LOADER, 0.5, () => {}, true)
      const suggestionBody: ReviewProps = {
        feature: 'fcaAd',
        comment: `AD: ${validationResult.adCode} - Freq: ${validationResult.frequency}`,
        type: 'suggestion',
        version: VERSION,
        product: 'planning-app'
      }

      const result = await new SendReviewUseCase().execute(suggestionBody)
      BackdropImperativeInterface.hide()

      if (result.isFailure) {
        GlobalToastUtil.send({
          label: t('TOAST_FCA-AD-SUGGESTION_ERROR'),
          variant: 'error',
          dismissible: true
        })
        return false
      }

      GlobalToastUtil.send({
        label: t('TOAST_FCA-AD-SUGGESTION_SUCCESS'),
        variant: 'primary',
        rightIcon: 'Check'
      })

      return true
    }

    ModalUtil.global.show({
      title: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_TITLE'),
      inputs: [
        {
          id: 'adCode',
          label: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_FIRST-FIELD'),
          placeholder: 'Ex: SKNX',
          value: '',
          valueToUpperCase: true,
          validate: (value) => validateFcaAdIcao(value, validationMessages)
        },
        {
          id: 'frequency',
          label: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_SECOND-FIELD'),
          placeholder: 'Ex: 135.40',
          value: '',
          validate: (value) => validateFcaAdFrequency(value, validationMessages)
        }
      ],
      maxWidth: 336,
      hasCloseButton: true,
      positiveColor: 'primary',
      onPositive: (_values) => {
        const adCode = _values.find((input) => input.id === 'adCode')?.value ?? ''
        const frequency = _values.find((input) => input.id === 'frequency')?.value ?? ''

        return handleSendFcaAdSuggestion(adCode, frequency)
      },
      positiveLabel: t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_MODAL_BUTTON')
    })
  }, [VERSION, t])

  return (
    <ToolContainer gap={12}>
      <MenuGroup>
        <MenuItem
          key="fir"
          title={t('TOOLBAR_AIRSPACES_ITEM_FIR_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.fir}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'fir',
              active: !mapLayerState.fir // active is the new state
            })
            handleAirspaces('fir', 'firSector')
          }}
        />
        <MenuItem
          key="firSector"
          title={t('TOOLBAR_AIRSPACES_ITEM_FIR-SECTOR_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.firSector}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'firSector',
              active: !mapLayerState.firSector // active is the new state
            })
            handleAirspaces('firSector', 'fir')
          }}
        />
      </MenuGroup>
      <MenuItem
        key="fis"
        title={t('TOOLBAR_AIRSPACES_ITEM_FIS_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.fis}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'fis',
            active: !mapLayerState.fis // active is the new state
          })
          handleAirspaces('fis')
        }}
      />
      <MenuGroup>
        <MenuItem
          key="tma"
          title={t('TOOLBAR_AIRSPACES_ITEM_TMA_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.tma}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'tma',
              active: !mapLayerState.tma // active is the new state
            })
            handleAirspaces('tma')
          }}
        />
        <MenuItem
          key="tmaSector"
          title={t('TOOLBAR_AIRSPACES_ITEM_TMA-SECTOR_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.tmaSector}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'tmaSector',
              active: !mapLayerState.tmaSector // active is the new state
            })
            handleAirspaces('tmaSector')
          }}
        />
      </MenuGroup>
      <MenuGroup>
        <MenuItem
          key="cta"
          title={t('TOOLBAR_AIRSPACES_ITEM_CTA_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.cta}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'cta',
              active: !mapLayerState.cta // active is the new state
            })
            handleAirspaces('cta')
          }}
        />
        <MenuItem
          key="ctaSector"
          title={t('TOOLBAR_AIRSPACES_ITEM_CTA-SECTOR_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.ctaSector}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'ctaSector',
              active: !mapLayerState.ctaSector // active is the new state
            })
            handleAirspaces('ctaSector')
          }}
        />
      </MenuGroup>
      <MenuItem
        key="ctr"
        title={t('TOOLBAR_AIRSPACES_ITEM_CTR_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.ctr}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'ctr',
            active: !mapLayerState.ctr // active is the new state
          })
          handleAirspaces('ctr')
        }}
      />
      <MenuItem
        key="atz"
        title={t('TOOLBAR_AIRSPACES_ITEM_ATZ_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.atz}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'atz',
            active: !mapLayerState.atz // active is the new state
          })
          handleAirspaces('atz', null, ['fiz', 'afis'])
        }}
      />
      <MenuItem
        key="fiz"
        title={t('TOOLBAR_AIRSPACES_ITEM_FIZ_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.fiz}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'fiz',
            active: !mapLayerState.fiz // active is the new state
          })
          handleAirspaces('fiz', null, ['atz', 'afis'])
        }}
      />
      <MenuItem
        key="afis"
        title={t('TOOLBAR_AIRSPACES_ITEM_AFIS_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.afis}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'afis',
            active: !mapLayerState.afis // active is the new state
          })
          handleAirspaces('afis', null, ['atz', 'fiz'])
        }}
      />
      <MenuGroup>
        <MenuItem
          key="fca"
          title={t('TOOLBAR_AIRSPACES_ITEM_FCA_REA_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.fca}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'fca',
              active: !mapLayerState.fca // active is the new state
            })
            handleAirspaces('fca', 'fcaReh')
          }}
        />
        <MenuItem
          key="fcaReh"
          title={t('TOOLBAR_AIRSPACES_ITEM_FCA_REH_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.fcaReh}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'fcaReh',
              active: !mapLayerState.fcaReh // active is the new state
            })
            handleAirspaces('fcaReh', 'fca')
          }}
        />
      </MenuGroup>
      <MenuGroup>
        <MenuItem
          key="fcaAd"
          title={t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_LABEL')}
          size="sm"
          hasSwitch
          isSwitchChecked={mapLayerState.fcaAd}
          onClick={() => {
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: 'fcaAd',
              active: !mapLayerState.fcaAd // active is the new state
            })
            handleAirspaces('fcaAd')
          }}
        />
        <MenuItem
          key="fcaAdSuggestion"
          title={t('TOOLBAR_AIRSPACES_ITEM_FCA_AD_SUGGESTION_LABEL')}
          size="sm"
          rightDecorator={
            <Icon>
              <Assets.Plus size={'sm'} color={'#FCFCFC'} />
            </Icon>
          }
          onClick={() => {
            handleShowSuggestionModal()
            // sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            //   extra: 'fcaAdSugger',
            // })
          }}
        />
      </MenuGroup>
      <MenuItem
        key="suaProhibited"
        title={t('TOOLBAR_AIRSPACES_ITEM_SUA-PROHIBITED_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.suaProhibited}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'suaProhibited',
            active: !mapLayerState.suaProhibited // active is the new state
          })
          handleAirspaces('suaProhibited')
        }}
      />
      <MenuItem
        key="suaRestricted"
        title={t('TOOLBAR_AIRSPACES_ITEM_SUA-RESTRICT_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.suaRestricted}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'suaRestricted',
            active: !mapLayerState.suaRestricted // active is the new state
          })
          handleAirspaces('suaRestricted')
        }}
      />
      <MenuItem
        key="suaDangerous"
        title={t('TOOLBAR_AIRSPACES_ITEM_SUA-DANGEROUS_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.suaDangerous}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'suaDangerous',
            active: !mapLayerState.suaDangerous // active is the new state
          })
          handleAirspaces('suaDangerous')
        }}
      />
      <MenuItem
        key="freeRouteAreas"
        title={t('TOOLBAR_AIRSPACES_ITEM_FREE-ROUTE-AREAS_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.freeRouteAreas}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'freeRouteAreas',
            active: !mapLayerState.freeRouteAreas // active is the new state
          })
          handleAirspaces('freeRouteAreas')
        }}
      />
      <MenuItem
        key="adiz"
        title={t('TOOLBAR_AIRSPACES_ITEM_ADIZ_LABEL')}
        size="sm"
        hasSwitch
        isSwitchChecked={mapLayerState.adiz}
        onClick={() => {
          sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
            extra: 'adiz',
            active: !mapLayerState.adiz // active is the new state
          })
          handleAirspaces('adiz')
        }}
      />
      {extraAirspaceTypes.map((type) => (
        <MenuItem
          key={type}
          title={getAirspaceMenuLabel(type)}
          size="sm"
          hasSwitch
          isSwitchChecked={!!mapLayerState.airspaceExtras?.[type]}
          onClick={() => {
            const active = !mapLayerState.airspaceExtras?.[type]
            sendMetrics(MetricsEvents.TOOLBAR_AIRSPACES_TYPE_SELECTED, {
              extra: type,
              active
            })
            mapLayerStateMutator.setAirspaceLayer(type, active)
          }}
        />
      ))}
    </ToolContainer>
  )
}

export { AirspacesTool }
